/**
 * Host Global - Property Rental Management System
 * Authentication Footer Component
 */

import PropTypes from "prop-types";
import Container from "@mui/material/Container";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import typography from "assets/theme/base/typography";
import { contactConfig } from "config/contact";

// Same as BasicLayout body background so footer matches the page
const AUTH_BODY_BACKGROUND = [
  "linear-gradient(135deg, #272e4e 0%, #1e2342 40%, #2a3152 70%, #272e4e 100%)",
  "radial-gradient(ellipse 80% 50% at 70% 20%, rgba(185, 145, 96, 0.18) 0%, transparent 50%)",
  "radial-gradient(ellipse 60% 40% at 20% 80%, rgba(185, 145, 96, 0.12) 0%, transparent 50%)",
].join(", ");

function Footer({ light }) {
  const { size } = typography;
  const { supportEmail, supportPhone } = contactConfig;
  const textColor = light ? "#ffffff" : "text";
  const linkOpacity = light ? 0.95 : 1;

  return (
    <MDBox
      width="100%"
      pt={{ xs: 2, sm: 2.5, md: 3 }}
      pb={{ xs: 2, sm: 2.5, md: 3 }}
      sx={{
        ...(light ? { color: "#ffffff" } : {}),
        ...(light
          ? {
              backgroundImage: AUTH_BODY_BACKGROUND,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }
          : { backgroundColor: "transparent" }),
        boxSizing: "border-box",
        paddingRight: { xs: 56, sm: 0 },
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 2 },
          maxWidth: "100%",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <MDBox
          width="100%"
          display="flex"
          flexDirection={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          alignItems="center"
          px={0}
          gap={{ xs: 1.5, sm: 2 }}
          flexWrap="nowrap"
          color={textColor}
          fontSize={{ xs: size.xs, sm: size.sm }}
          textAlign={{ xs: "center", lg: "left" }}
          sx={{
            maxWidth: "100%",
            "& a": { wordBreak: "break-all", whiteSpace: "normal" },
          }}
        >
          <MDBox
            component="span"
            display="block"
            color={textColor}
            fontSize={{ xs: size.xs, sm: size.sm }}
            sx={{
              ...(light ? { color: "#ffffff" } : {}),
              whiteSpace: "normal",
              wordBreak: "break-word",
              width: "100%",
              maxWidth: "100%",
              flexShrink: 0,
            }}
          >
            &copy; {new Date().getFullYear()} Hosta Global. All rights reserved.
          </MDBox>
          <MDBox
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems="center"
            gap={1.5}
            flexWrap="nowrap"
            justifyContent="center"
            sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
          >
            <MDTypography
              component="a"
              href={`mailto:${supportEmail}`}
              variant="button"
              fontWeight="regular"
              fontSize={{ xs: size.xs, sm: size.sm }}
              sx={{
                color: textColor,
                opacity: linkOpacity,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <Icon sx={{ fontSize: "1rem", color: "inherit" }}>email</Icon>
              {supportEmail}
            </MDTypography>
            <MDTypography
              component="a"
              href={`tel:${supportPhone.replace(/\s/g, "")}`}
              variant="button"
              fontWeight="regular"
              fontSize={{ xs: size.xs, sm: size.sm }}
              sx={{
                color: textColor,
                opacity: linkOpacity,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <Icon sx={{ fontSize: "1rem", color: "inherit" }}>phone</Icon>
              {supportPhone}
            </MDTypography>
          </MDBox>
        </MDBox>
      </Container>
    </MDBox>
  );
}

// Setting default props for the Footer
Footer.defaultProps = {
  light: false,
};

// Typechecking props for the Footer
Footer.propTypes = {
  light: PropTypes.bool,
};

export default Footer;
